// Danone risk assessment model - single source of truth for scores and RAG.
//
// Both axes use the same 4-level scale (scores 1-4); the risk rating is the
// product (1-16), banded into red / amber / green. Mirrors the standard
// Danone risk register so exports line up with what leadership expects:
//
//              | Very Low(1)  Low(2)  High(3)  Very High(4)   <- likelihood
//   Very High 4|     4          8       12         16
//   High      3|     3          6        9         12
//   Low       2|     2          4        6          8
//   Very Low  1|     1          2        3          4
//   ^ impact
//
// Bands: red >= 9, amber 4-8, green <= 3.

import type { RiskEntry, RiskLevel, RiskSeverity, RiskCategory } from '../types'

export const RISK_LEVELS: RiskLevel[] = ['very_low', 'low', 'high', 'very_high']

export const RISK_LEVEL_SCORE: Record<RiskLevel, number> = {
  very_low: 1,
  low: 2,
  high: 3,
  very_high: 4,
}

export const RISK_LEVEL_LABEL: Record<RiskLevel, string> = {
  very_low: 'Very Low',
  low: 'Low',
  high: 'High',
  very_high: 'Very High',
}

// Guidance thresholds from the Danone risk register (impact on budget /
// timeline; likelihood = probability the cause leads to the effect).
export const IMPACT_HINTS: Record<RiskLevel, string> = {
  very_low: '<€10K or <5% budget / <2 weeks',
  low: '€10-25K or 5-10% / 2-5 weeks',
  high: '€25-50K or 10-15% / 5-10 weeks',
  very_high: '>€50K or >15% / >10 weeks',
}

export const LIKELIHOOD_HINTS: Record<RiskLevel, string> = {
  very_low: '<2% chance',
  low: '2-5% chance',
  high: '5-10% chance',
  very_high: '>10% chance',
}

export type RiskRag = 'green' | 'amber' | 'red'

/** Risk rating = impact score x likelihood score, or null when the risk
 *  predates the matrix and hasn't been re-assessed yet. */
export function riskScore(r: Pick<RiskEntry, 'impact' | 'likelihood'>): number | null {
  if (!r.impact || !r.likelihood) return null
  return RISK_LEVEL_SCORE[r.impact] * RISK_LEVEL_SCORE[r.likelihood]
}

export function ragForScore(score: number): RiskRag {
  if (score >= 9) return 'red'
  if (score >= 4) return 'amber'
  return 'green'
}

// Legacy severity -> RAG so pre-matrix risks keep a sensible colour until
// they're re-assessed (editing a legacy risk requires the new fields).
const LEGACY_SEVERITY_RAG: Record<RiskSeverity, RiskRag> = {
  low: 'green',
  medium: 'amber',
  high: 'red',
}

/** RAG for any risk entry, old or new. Null only when the entry has neither
 *  a matrix assessment nor a legacy severity. */
export function riskRag(r: Pick<RiskEntry, 'impact' | 'likelihood' | 'severity'>): RiskRag | null {
  const score = riskScore(r)
  if (score !== null) return ragForScore(score)
  return r.severity ? LEGACY_SEVERITY_RAG[r.severity] : null
}

/** The risk's effective 1-16 rating. For assessed risks it's impact x
 *  likelihood; for legacy severity-only risks it maps to the midpoint of the
 *  matching RAG band (high->9, medium->4, low->1) so old and new risks sort
 *  and threshold consistently. Drives both list ordering and the forecast
 *  materiality gate (hasMaterialOpenRisk). */
export function effectiveRiskScore(r: Pick<RiskEntry, 'impact' | 'likelihood' | 'severity'>): number {
  const score = riskScore(r)
  if (score !== null) return score
  return r.severity === 'high' ? 9 : r.severity === 'medium' ? 4 : 1
}

// Minimum effective rating an OPEN risk must reach to project a KPI miss in
// the Forecast view. 4 = Amber-or-worse (recommended): Green risks (<=3) are
// genuinely low and shouldn't move the leadership headline. PMO-tunable via
// Settings (riskForecastMinScore).
export const DEFAULT_RISK_FORECAST_MIN_SCORE = 4

/** True when the project carries an OPEN risk of `category` material enough
 *  (effective rating >= minScore) to project a Forecast KPI miss. This is the
 *  bridge from the risk log to OTIFAC: delivery->On Time, scope->In Full,
 *  cost->At Cost. */
export function hasMaterialOpenRisk(
  risks: RiskEntry[] | undefined,
  category: RiskCategory,
  minScore: number = DEFAULT_RISK_FORECAST_MIN_SCORE,
): boolean {
  return (risks ?? []).some(
    r => r.status === 'open' && r.category === category && effectiveRiskScore(r) >= minScore,
  )
}

/** Human label for a forecast threshold, used in tooltips/notes so the copy
 *  stays truthful when PMO changes the setting. */
export function riskForecastBandLabel(minScore: number): string {
  if (minScore >= 9) return 'Red'
  if (minScore >= 4) return 'Amber or Red'
  return 'any rated'
}

// Shared Tailwind colour tokens (Danone palette - same hexes used across the app).
export const RAG_BAR: Record<RiskRag, string> = {
  red: 'bg-[#EF3340]',
  amber: 'bg-[#F88806]',
  green: 'bg-[#00843D]',
}

export const RAG_TEXT: Record<RiskRag, string> = {
  red: 'text-[#B8242E]',
  amber: 'text-[#B8640B]',
  green: 'text-[#00843D]',
}

export const RAG_PILL: Record<RiskRag, string> = {
  red: 'bg-[#EF3340]/10 text-[#B8242E]',
  amber: 'bg-[#F88806]/10 text-[#B8640B]',
  green: 'bg-[#00843D]/10 text-[#00843D]',
}

export const RAG_LABEL: Record<RiskRag, string> = {
  red: 'Red',
  amber: 'Amber',
  green: 'Green',
}

/** Count open risks by RAG band - the "how many reds/ambers/greens" rollup. */
export function ragCounts(risks: Pick<RiskEntry, 'impact' | 'likelihood' | 'severity' | 'status'>[]): Record<RiskRag, number> {
  const counts: Record<RiskRag, number> = { red: 0, amber: 0, green: 0 }
  for (const r of risks) {
    if (r.status !== 'open') continue
    const rag = riskRag(r)
    if (rag) counts[rag]++
  }
  return counts
}
